import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  ArrowRight,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import { useOptionalProject } from "@/components/project-context";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { cn } from "@/lib/utils";

interface SyncQueueWidgetProps {
  isMobile?: boolean;
}

export function SyncQueueWidget({ isMobile = false }: SyncQueueWidgetProps) {
  const project = useOptionalProject();
  const [copied, setCopied] = useState(false);
  const projectId = project?.id ?? "";

  const { rows, isLoading } = useWorkRequests(projectId);

  const claimedCount = useMemo(
    () => rows.filter((r) => r.status === "claimed").length,
    [rows],
  );
  const queuedCount = useMemo(
    () => rows.filter((r) => r.status === "queued").length,
    [rows],
  );

  const totalActive = claimedCount + queuedCount;

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText("npx @iamu-dev/iamu pull");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore clipboard permission rejections
    }
  };

  if (!project) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={
            isMobile ? "sync-queue-widget-mobile" : "sync-queue-widget-desktop"
          }
          className={cn(
            "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors border",
            isMobile
              ? claimedCount > 0
                ? "bg-sidebar-foreground text-sidebar border-sidebar-foreground shadow-xs"
                : queuedCount > 0
                  ? "bg-sidebar-accent text-sidebar-foreground border-sidebar-border"
                  : "bg-transparent text-sidebar-muted-foreground border-sidebar-border/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              : claimedCount > 0
                ? "bg-foreground text-background border-foreground shadow-xs"
                : queuedCount > 0
                  ? "bg-card text-foreground border-border hover:bg-muted"
                  : "bg-transparent text-muted-foreground border-border/60 hover:text-foreground hover:bg-muted/50",
          )}
          aria-label={`Sync queue: ${totalActive} active items`}
        >
          {claimedCount > 0 ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Bot className="h-3.5 w-3.5" aria-hidden="true" />
          )}

          {!isMobile && (
            <span>
              {isLoading
                ? "Loading…"
                : claimedCount > 0
                  ? `${claimedCount} running`
                  : queuedCount > 0
                    ? `${queuedCount} queued`
                    : "Queue idle"}
            </span>
          )}

          {isMobile && totalActive > 0 && (
            <span className="tabular-nums font-semibold">{totalActive}</span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={6}
        collisionPadding={12}
        className="w-[calc(100vw-1.5rem)] max-w-xs sm:w-80 p-0 shadow-lg border-border"
      >
        {/* Header */}
        <div className="p-3.5 border-b border-border/80 space-y-1">
          <div className="flex items-center justify-between">
            <Kicker>Agent sync queue</Kicker>
            <span className="text-2xs font-medium text-muted-foreground">
              Pull-based
            </span>
          </div>
          <p className="text-xs text-foreground font-semibold">
            {totalActive === 0
              ? "No tasks currently queued"
              : `${totalActive} task${totalActive === 1 ? "" : "s"} waiting for local pull`}
          </p>
        </div>

        {/* Educational banner explaining pull requirement */}
        <div className="p-3 bg-muted/40 border-b border-border/60 text-xs text-muted-foreground space-y-2">
          <p>
            The cloud does not push changes to developer machines. Run{" "}
            <code className="text-foreground font-mono font-medium">
              iamu pull
            </code>{" "}
            locally or prompt your AI agent to pick up work.
          </p>

          <div className="flex items-center justify-between gap-2 rounded bg-background border border-border px-2 py-1 font-mono text-2xs text-foreground">
            <span className="truncate">$ npx @iamu-dev/iamu pull</span>
            <button
              type="button"
              onClick={handleCopyCommand}
              className="text-muted-foreground hover:text-foreground transition-colors size-7 flex items-center justify-center rounded hover:bg-muted shrink-0"
              aria-label="Copy pull command"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>

        {/* Task list preview */}
        <div className="p-2 space-y-1 max-h-56 overflow-y-auto">
          {isLoading ? (
            <div className="py-4 text-center text-xs text-muted-foreground">
              Loading queue...
            </div>
          ) : rows.length === 0 ? (
            <div className="py-4 px-2 text-center text-xs text-muted-foreground space-y-1">
              <p>Queue is empty.</p>
              <p className="text-2xs">
                Queue fixes from findings or run reviews to send work to local
                agents.
              </p>
            </div>
          ) : (
            rows.slice(0, 4).map((row) => (
              <div
                key={row.id}
                className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors text-xs"
              >
                {row.findingSeverity && (
                  <SeverityDot
                    tone={severityTone(row.findingSeverity as any)}
                    className="mt-1"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">
                    {row.canonicalTitle ?? row.scenarioName ?? "Work request"}
                  </p>
                  <p className="text-2xs text-muted-foreground truncate">
                    {row.status === "claimed"
                      ? "In agent session"
                      : `Queued ${row.kind}`}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-2.5 border-t border-border/80 bg-muted/20 flex items-center justify-between text-xs">
          <Link
            to={`/projects/${projectId}`}
            className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 font-medium"
          >
            Dashboard
            <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to="/settings?tab=integrations"
            className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            Sync setup
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
